<template>
	<div class="form-viewer">
		<header class="form-viewer-toolbar">
			<div class="form-identity">
				<span class="form-mark" aria-hidden="true">
					<portal-icon name="file-check" :size="19"></portal-icon>
				</span>
				<div class="form-title">
					<h1>{{ submission.formName }}</h1>
					<span v-if="submission.submittedAt">
						{{ DateTime.fromISO(submission.submittedAt).toLocaleString(DateTime.DATETIME_MED) }}
					</span>
				</div>
			</div>

			<button
				type="button"
				class="round-action"
				:aria-label="$t('global.close')"
				:title="$t('global.close')"
				@click="handleClose"
			>
				<portal-icon name="x" :size="19"></portal-icon>
			</button>
		</header>

		<div class="form-frame-shell">
			<div v-if="!isReady" class="form-frame-loading">
				<v-progress-circular indeterminate :size="38" :width="3" color="primary"></v-progress-circular>
			</div>
			<main v-else class="form-result-body show-scrollbar">
				<section v-if="hasContactDetails" class="contact-card">
					<div v-if="contactName" class="contact-row">
						<span class="detail-icon"><portal-icon name="user-round" :size="17"></portal-icon></span>
						<strong>{{ contactName }}</strong>
					</div>
					<a v-if="formData.email" class="contact-row" :href="`mailto:${formData.email}`">
						<span class="detail-icon"><portal-icon name="mail" :size="17"></portal-icon></span>
						<strong>{{ formData.email }}</strong>
					</a>
					<a v-if="formData.phone" class="contact-row" :href="`tel:${formData.phone}`">
						<span class="detail-icon"><portal-icon name="phone" :size="17"></portal-icon></span>
						<strong>{{ formData.phone }}</strong>
					</a>
					<div v-if="formData.businessName" class="contact-row">
						<span class="detail-icon"><portal-icon name="folder" :size="17"></portal-icon></span>
						<strong>{{ formData.businessName }}</strong>
					</div>
					<div v-if="formattedAddress" class="contact-row">
						<span class="detail-icon"><portal-icon name="map-pin" :size="17"></portal-icon></span>
						<strong>{{ formattedAddress }}</strong>
					</div>
					<a
						v-if="formData.website"
						class="contact-row"
						:href="websiteUrl"
						target="_blank"
						rel="noopener"
					>
						<span class="detail-icon"><portal-icon name="external-link" :size="17"></portal-icon></span>
						<strong>{{ formData.website }}</strong>
					</a>
				</section>

				<section v-if="answers.length" class="answer-grid">
					<article v-for="(answer, index) in answers" :key="answer.id || index" class="answer-card">
						<span>{{ questionText(answer.question) }}</span>
						<p>{{ formatAnswer(answer.answer) }}</p>
					</article>
				</section>

				<section v-if="files.length" class="file-list">
					<a
						v-for="file in files"
						:key="file.id || file.fileName"
						:href="file.signedUrl"
						target="_blank"
						rel="noopener"
						class="file-row"
					>
						<span class="detail-icon"><portal-icon name="paperclip" :size="17"></portal-icon></span>
						<strong>{{ file.fileName }}</strong>
						<portal-icon name="external-link" :size="15"></portal-icon>
					</a>
				</section>
			</main>
		</div>
	</div>
</template>

<script>
	import { DateTime } from 'luxon';
	import { questionText } from '@/modules/forms/v2/schema/questionText';
	import FormService from '@/modules/forms/FormService';

	export default {
		name: 'FormResultViewer',

		props: ['submission'],

		components: {},

		data: function() {
			return {
				DateTime: DateTime,
				formService: new FormService(),
				formDetails: null,
				isReady: false,
			};
		},

		mounted() {
			this.loadSubmission();
		},

		methods: {
			questionText,
			handleClose: function() {
				this.$emit('result');
			},

			loadSubmission() {
				if (!this.submission.id) {
					this.formDetails = this.submission;
					this.isReady = true;
					return;
				}

				this.formService
					.getFormSubmission(this.submission.id)
					.then((res) => {
						this.formDetails = res.data;
					})
					.catch(() => {
						this.formDetails = this.submission;
					})
					.finally(() => {
						this.isReady = true;
					});
			},

			formatAnswer(answer) {
				if (answer === null || typeof answer === 'undefined' || answer === '') {
					return '—';
				}

				if (Array.isArray(answer)) {
					return answer.join(', ');
				}

				const value = String(answer);
				if (value.includes('||')) {
					return value.split('||').join(', ');
				}

				try {
					const parsed = JSON.parse(value);
					if (Array.isArray(parsed)) {
						return parsed.join(', ');
					}
				} catch (err) {
					// The majority of answers are plain text, not JSON.
				}

				return value;
			},
		},

		computed: {
			activeSubmission() {
				return this.formDetails || this.submission;
			},

			formData() {
				return this.activeSubmission.formData || {};
			},

			answers() {
				return Array.isArray(this.formData.answers) ? this.formData.answers : [];
			},

			files() {
				return Array.isArray(this.activeSubmission.files) ? this.activeSubmission.files : [];
			},

			contactName() {
				return [this.formData.firstName, this.formData.lastName].filter(Boolean).join(' ');
			},

			formattedAddress() {
				return [
					this.formData.address1,
					this.formData.address2,
					this.formData.city,
					this.formData.locality,
					this.formData.postal,
					this.formData.country,
				]
					.filter(Boolean)
					.join(', ');
			},

			websiteUrl() {
				if (!this.formData.website) {
					return null;
				}
				return /^https?:\/\//i.test(this.formData.website)
					? this.formData.website
					: `https://${this.formData.website}`;
			},

			hasContactDetails() {
				return Boolean(
					this.contactName ||
						this.formData.email ||
						this.formData.phone ||
						this.formData.businessName ||
						this.formattedAddress ||
						this.formData.website
				);
			},

		},
	};
</script>

<style scoped lang="scss">
	.form-viewer {
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background: var(--portal-canvas);
		color: var(--portal-text);
	}

	.form-viewer-toolbar {
		min-height: 68px;
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 10px 14px 10px 18px;
		border-bottom: 1px solid var(--portal-border);
		background: var(--portal-surface);
	}

	.form-identity {
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 11px;
	}

	.form-mark {
		width: 38px;
		height: 38px;
		flex: 0 0 38px;
		display: grid;
		place-items: center;
		border-radius: 12px;
		background: color-mix(in srgb, var(--v-primary-base) 10%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.form-title {
		min-width: 0;
		display: grid;
		gap: 2px;
	}

	.form-title h1 {
		margin: 0;
		overflow: hidden;
		color: var(--portal-text);
		font-size: 17px;
		font-weight: 680;
		line-height: 1.25;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.form-title span {
		color: var(--portal-muted);
		font-size: 12px;
	}

	.round-action {
		width: 38px;
		height: 38px;
		flex: 0 0 38px;
		display: grid;
		place-items: center;
		padding: 0;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 50%);
		background: var(--portal-surface);
		color: var(--portal-muted);
		cursor: pointer;
		transition: border-color 140ms ease, color 140ms ease, background-color 140ms ease;
	}

	.round-action:hover,
	.round-action:focus-visible {
		outline: 0;
		border-color: color-mix(in srgb, var(--portal-muted) 55%, var(--portal-border));
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.form-frame-shell {
		min-height: 0;
		flex: 1;
		position: relative;
		overflow: auto;
		background: var(--portal-surface);
	}

	.form-frame-loading {
		position: absolute;
		inset: 0;
		z-index: 1;
		display: grid;
		place-items: center;
		background: var(--portal-surface);
	}

	.form-result-body {
		width: min(840px, calc(100% - 48px));
		min-height: 100%;
		display: grid;
		align-content: start;
		gap: 16px;
		margin: 0 auto;
		padding: 28px 0 40px;
		text-align: left;
	}

	.contact-card,
	.answer-grid,
	.file-list {
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
	}

	.contact-card {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 0 20px;
		padding: 8px 16px;
	}

	.contact-row,
	.file-row {
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 11px 0;
		color: var(--portal-text);
		text-decoration: none;
	}

	.contact-row strong,
	.file-row strong {
		min-width: 0;
		overflow: hidden;
		font-size: 13px;
		font-weight: 560;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	a.contact-row:hover strong,
	a.file-row:hover strong {
		color: var(--v-primary-base);
	}

	.detail-icon {
		width: 32px;
		height: 32px;
		flex: 0 0 32px;
		display: grid;
		place-items: center;
		border-radius: 10px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
	}

	.answer-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		overflow: hidden;
	}

	.answer-card {
		min-width: 0;
		padding: 16px 18px;
		text-align: left;
	}

	.answer-card + .answer-card {
		border-top: 1px solid var(--portal-border);
	}

	.answer-card span {
		display: block;
		margin-bottom: 5px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 620;
		line-height: 1.35;
	}

	.answer-card p {
		margin: 0;
		color: var(--portal-text);
		font-size: 14px;
		line-height: 1.55;
		overflow-wrap: anywhere;
		white-space: pre-wrap;
	}

	.file-list {
		padding: 4px 16px;
	}

	.file-row + .file-row {
		border-top: 1px solid var(--portal-border);
	}

	.file-row > .portal-icon:last-child {
		margin-left: auto;
		color: var(--portal-muted);
	}

	@media (max-width: 599px) {
		.form-viewer-toolbar {
			min-height: 62px;
			padding-left: 12px;
		}

		.form-mark {
			width: 34px;
			height: 34px;
			flex-basis: 34px;
		}

		.form-result-body {
			width: calc(100% - 24px);
			padding: 16px 0 28px;
		}

		.contact-card {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>
